import { useEffect, useMemo, useState } from 'react';
import { Like, ContentType } from '@novaheal/types';
import { useLikes, useAddLike, useRemoveLike } from 'hooks/api/backend/user/like/likes';
import { useFirebase } from 'hooks/useFirebase';

type UseIsLiked = {
  isLiked: boolean | null;
  handleLikeChange: (title: string) => Promise<void>;
};

export const useIsLiked = (
  id: string | undefined,
  type: ContentType,
  // Set by callers that already know the answer without asking the server
  // (e.g. an item rendered on the Favorites page is liked by definition) —
  // skips the GET /user/likes lookup entirely rather than just ignoring it.
  entryIsLiked?: boolean
): UseIsLiked => {
  const shouldLookUp = entryIsLiked === undefined;
  const [isLiked, setIsLiked] = useState<boolean | null>(entryIsLiked ?? null);
  const updateLikeMutation = useAddLike();
  const removeLikeMutation = useRemoveLike();
  const { data: allLikes } = useLikes(shouldLookUp);
  const { logFirebaseClick } = useFirebase();

  // One rendered list can mount dozens of LikeButtons off the same cached
  // `allLikes` payload — build the lookup set once per (allLikes, type)
  // change instead of each button doing its own O(n) .filter() scan.
  const likedIdSet = useMemo(() => {
    if (!allLikes) return null;
    const likesToCheck =
      type === ContentType.ARTICLE ? allLikes.likedArticles : allLikes.likedMedications;
    return new Set(likesToCheck.map((like: Like) => like.contentfulId));
  }, [allLikes, type]);

  useEffect(() => {
    if (!shouldLookUp) {
      setIsLiked(entryIsLiked ?? null);
      return;
    }
    if (likedIdSet && id) {
      setIsLiked(likedIdSet.has(id));
    }
  }, [shouldLookUp, entryIsLiked, likedIdSet, id]);

  const handleLikeChange = async (title: string): Promise<void> => {
    if (!id) return;
    // useAddLike/useRemoveLike patch the shared `['likes']` cache directly
    // on success, so every other mounted LikeButton picks up the change
    // without a refetch — no need to also refetch here. On failure their
    // onError shows a toast; mutateAsync still rejects after that, so we
    // catch it here — otherwise it's an unhandled rejection all the way up
    // through LikeButton's click handler, and `setIsLiked` below never
    // fires, which is correct: the button should reflect the failed state.
    try {
      if (isLiked) {
        logFirebaseClick('remove-like', title, { contentType: type });
        await removeLikeMutation.mutateAsync({ id, type });
        setIsLiked(false);
      } else {
        logFirebaseClick('add-like', title, { contentType: type });
        await updateLikeMutation.mutateAsync({ id, type });
        setIsLiked(true);
      }
    } catch {
      // Already surfaced via toast in useAddLike/useRemoveLike's onError.
    }
  };

  return { isLiked, handleLikeChange };
};
